import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/apiClient";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Upload, Home } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export function HomeSettings() {
  const [home, setHome] = useState(null);
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  useEffect(() => { api.get("/settings").then(({ data }) => setHome(data.home)); }, []);

  const f = (k) => ({ value: home[k], onChange: (e) => setHome({ ...home, [k]: e.target.value }) });
  const hl = (i, k, v) => setHome({ ...home, highlights: home.highlights.map((h, j) => (j === i ? { ...h, [k]: v } : h)) });

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setHome((h) => ({ ...h, hero_image: BACKEND + data.url }));
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setUploading(false); }
  };

  const save = async () => {
    setBusy(true);
    try { await api.put("/settings", { home }); toast.success("Page d'accueil mise à jour"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  if (!home) return null;
  return (
    <div className="rounded-2xl bg-white border border-border p-5" data-testid="home-settings">
      <h2 className="font-serif text-2xl text-olive flex items-center gap-2"><Home className="w-5 h-5 text-rose-brand" /> Page d'accueil</h2>
      <p className="text-sm text-muted-foreground mt-1">Textes, photo d'ambiance et coordonnées affichés sur la page d'accueil et le pied de page.</p>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
        <div className="md:col-span-1">
          <Label className="text-xs">Photo d'ambiance (haut de page)</Label>
          <div className="mt-1 aspect-[4/3] rounded-xl overflow-hidden bg-secondary relative">
            {home.hero_image && <img src={home.hero_image} alt="" className="w-full h-full object-cover" data-testid="home-hero-preview" />}
            <label className="absolute bottom-2 right-2 inline-flex items-center gap-1 text-xs bg-white/90 rounded-full px-3 py-1.5 cursor-pointer border border-border hover:bg-white">
              <Upload className="w-3.5 h-3.5" /> {uploading ? "Envoi…" : "Changer"}
              <input type="file" accept="image/*" onChange={upload} className="hidden" data-testid="home-hero-input" />
            </label>
          </div>
        </div>
        <div className="md:col-span-2 space-y-3">
          <div><Label className="text-xs">Petite accroche (au-dessus du titre)</Label><Input {...f("tagline")} data-testid="home-tagline" /></div>
          <div><Label className="text-xs">Titre principal</Label><Input {...f("headline")} data-testid="home-headline" /></div>
          <div><Label className="text-xs">Présentation</Label><Textarea rows={3} {...f("intro")} data-testid="home-intro" /></div>
          <div><Label className="text-xs">Texte de bienvenue (long, affiché sous la recherche)</Label><Textarea rows={8} {...f("about")} data-testid="home-about" /></div>
          <div><Label className="text-xs">Destinations proposées dans la recherche (séparées par des virgules)</Label><Input value={(home.destinations || []).join(", ")} onChange={(e) => setHome({ ...home, destinations: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) })} data-testid="home-destinations" /></div>
        </div>
      </div>
      <Label className="text-xs mt-4 block">Points forts</Label>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-1">
        {home.highlights.map((h, i) => (
          <div key={i} className="space-y-1 p-3 rounded-xl bg-secondary/60">
            <Input value={h.title} onChange={(e) => hl(i, "title", e.target.value)} placeholder="Titre" className="bg-white" data-testid={`home-highlight-title-${i}`} />
            <Input value={h.text} onChange={(e) => hl(i, "text", e.target.value)} placeholder="Une phrase" className="bg-white" />
          </div>
        ))}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mt-4">
        <div><Label className="text-xs">Localisation</Label><Input {...f("location")} placeholder="Ex : Forêt de Fontainebleau (77)" data-testid="home-location" /></div>
        <div><Label className="text-xs">Téléphone</Label><Input {...f("phone")} placeholder="+33 6 …" data-testid="home-phone" /></div>
        <div><Label className="text-xs">Email de contact</Label><Input {...f("contact_email")} placeholder="contact@…" data-testid="home-contact-email" /></div>
        <div><Label className="text-xs">Texte pied de page</Label><Input {...f("footer_text")} /></div>
        <div className="md:col-span-2"><Label className="text-xs">Lien vers vos avis Google (affiché sur les fiches)</Label><Input {...f("google_reviews_url")} placeholder="https://g.page/r/…/review ou lien Google Maps" data-testid="home-google-reviews" /></div>
      </div>
      <Button onClick={save} disabled={busy} className="mt-4 bg-olive text-white" data-testid="home-save-button">{busy ? "..." : "Enregistrer"}</Button>
    </div>
  );
}
